// Thin fetch wrapper. Throws on non-2xx so call sites can use try/catch
// or treat the resolved value as the parsed JSON body of type T.

// Structured error bodies the backend emits for self-describing
// failures. The wrapper checks for these so the per-feature catch
// sites can show the friendly `message` straight to the user instead
// of the generic "API 401 on /api/...: ..." form, and so they can
// branch on the well-known `code` if they want a tailored CTA.
interface StructuredErrorBody {
  code?: unknown;
  message?: unknown;
}

function parseErrorBody(body: string): { code: string | null; message: string | null } {
  if (!body) return { code: null, message: null };
  try {
    const parsed = JSON.parse(body) as StructuredErrorBody;
    if (!parsed || typeof parsed !== "object") return { code: null, message: null };
    return {
      code: typeof parsed.code === "string" ? parsed.code : null,
      message: typeof parsed.message === "string" ? parsed.message : null,
    };
  } catch {
    return { code: null, message: null };
  }
}

export class ApiError extends Error {
  // Stable identifier for known backend failures (e.g. "reauth-needed").
  // Null when the response body wasn't a structured `{ code, message }`
  // payload — catch sites that just surface `error.message` get the
  // friendly text without caring about the code.
  public readonly code: string | null;

  constructor(
    public readonly status: number,
    public readonly path: string,
    public readonly body: string,
  ) {
    const parsed = parseErrorBody(body);
    super(parsed.message ?? `API ${status} on ${path}: ${body.slice(0, 200)}`);
    this.name = "ApiError";
    this.code = parsed.code;
  }
}

export async function fetchJson<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(path, init);
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new ApiError(res.status, path, body);
  }
  return (await res.json()) as T;
}
